import { forkJoin, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AzureManager } from '../azure/azure-manager';
import { ErrorMonitor } from '../diagnostics/error-monitor';
import { Logging } from '../diagnostics/logging';
import { FrameConnection } from '../frame/frame-connection';
import { NotificationConnection } from '../notification/notification-connection';
import { WorkItemConnection } from '../notification/work-item-connection';
import { PerformanceProfile } from '../performance/performance-profile';
import { PerformanceTracker } from '../performance/performance-tracker';
import { Rpc } from '../rpc/rpc';
import { ActiveConnection } from '../security/active-connection';
import { AuthorizationManager } from '../security/authorization-manager';
import { ConnectionManager } from '../security/connection-manager';
import { CredSSPManager } from '../security/credssp-manager';
import { CimConnection } from './cim-connection';
import { CimStream } from './cim-stream';
import { FileTransfer } from './file-transfer';
import { GatewayConnection } from './gateway-connection';
import { NodeConnection } from './node-connection';
import { PowerShellConnection } from './powershell-connection';
import { PowerShellStream } from './powershell-stream';
import { ResourceCache } from './resource-cache';
import { SettingsManager } from './settings-manager';
import { SshStream } from './ssh-stream';

/**
 * The application context class.
 */
export class AppContext {
    /**
     * Initializes a new instance of the AppContext class.
     *
     * @param activeConnection the active connection.
     * @param authorizationManager the authorization manager.
     * @param cim the cim connection.
     * @param cimStream the cim stream.
     * @param connectionManager the connection manager.
     * @param fileTransfer the file transfer connection.
     * @param frame the frame connection.
     * @param gateway the gateway connection.
     * @param node the node connection.
     * @param notification the notification connection.
     * @param powerShell the powerShell connection.
     * @param powerShellStream the powerShell stream.
     * @param resourceCache the resource cache.
     * @param rpc the Rpc.
     * @param settingsManager the user profile.
     * @param sshStream the SSH stream
     * @param workItem the work item connection
     */
    constructor(
        public activeConnection: ActiveConnection,
        public authorizationManager: AuthorizationManager,
        public azureManagerService: AzureManager,
        public cim: CimConnection,
        public cimStream: CimStream,
        public connectionManager: ConnectionManager,
        public credSSPManager: CredSSPManager,
        public fileTransfer: FileTransfer,
        public frame: FrameConnection,
        public gateway: GatewayConnection,
        public node: NodeConnection,
        public notification: NotificationConnection,
        public powerShell: PowerShellConnection,
        public powerShellStream: PowerShellStream,
        public resourceCache: ResourceCache,
        public rpc: Rpc,
        public settingsManager: SettingsManager,
        public sshStream: SshStream,
        public workItem: WorkItemConnection
    ) {
        PerformanceTracker.appContextServiceStarted();
        Logging.current.registerRpc(this.rpc, this.gateway);
        PerformanceProfile.current.registerRpc(this.rpc);
        ErrorMonitor.current.registerRpc(this.rpc);
        PerformanceTracker.appContextRpcInitStarted();
        rpc.init();
        PerformanceTracker.appContextRpcInitComplete();
    }

    public get servicesReady(): Observable<boolean> {
        if (!MsftSme.self().Environment.configuration.gateway.disabled) {
            return forkJoin([
                this.authorizationManager.initialize(),
                this.connectionManager.initialize(),
                this.gateway.initialize()])
                .pipe(
                    map(() => true),
                    take(1));
        }

        return of(true);
    }

    /**
     * Reinitialized all forwarder services to get latest data.
     */
    public reinitializeServices(): Observable<boolean> {
        return forkJoin([
            this.authorizationManager.reinitializeForwardedData(),
            this.connectionManager.reinitializeForwardedData(),
            this.gateway.initialize()
        ]).pipe(map(() => true));
    }
}
